import { cloneDeep, isEmpty, get } from 'lodash';
import * as moment from 'moment';
import {
    CandidateEvaluationEnum,
    CandidateEvaluationEnum2LabelMapping,
    GenderEnum,
    GenderEnum2LabelMapping,
    ResultEvaluationTypeEnum,
} from '../../../shared/models/campaign.model';
import { TestResultCellRendererComponent } from '../campaign-running/campaign-running-steps/shared/test-result-cell-renderer.component';
import { ColDef } from 'ag-grid-community';
import {
    Address,
    CandidateProfile,
    Certificate,
    Experience,
    HeaderTranslation,
    Prerequisite,
    ProfileImage,
    RoundData,
    SortCriteria,
    TestResultTableRow,
    TranslationsMap,
} from './campaign-utils.types';

export default class CampaignUtils {
    // Map backend health certificate types to translation keys
    static healthCertificateTypeMapping: Record<string, string> = {
        'Loại 1 (rất tốt)': 'HEALTH_CERTIFICATE.TYPE_1',
        'Loại 2 (tốt)': 'HEALTH_CERTIFICATE.TYPE_2',
        'Loại 3 (khá)': 'HEALTH_CERTIFICATE.TYPE_3',
        'Loại 4 (trung bình)': 'HEALTH_CERTIFICATE.TYPE_4',
        'Loại 5 (kém)': 'HEALTH_CERTIFICATE.TYPE_5',
    };

    static checkBoxCol: ColDef = {
        field: 'selector',
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 30,
        suppressNavigable: true,
        cellStyle: { 'margin-top': '7px' },
    };

    static defaultColDef: ColDef = {
        sortable: true,
        filter: false,
        resizable: true,
        sortingOrder: ['desc', 'asc', null],
    };

    static genericTestResultColDefs: ColDef[] = [
        {
            // valueGetter: 'node.rowIndex + 1',
            field: 'order',
            headerName: '#',
            width: 50,
            sortable: false,
            cellStyle: { 'text-align': 'center' },
            cellClass: 'center-cell-class',
        },
        {
            field: 'avatar',
            cellRenderer: TestResultCellRendererComponent,
            headerName: 'Hình thẻ',
            width: 72,
            sortable: false,
            autoHeight: true,
            cellStyle: { 'text-align': 'center' },
        },
        {
            field: 'fullName',
            cellRenderer: TestResultCellRendererComponent,
            headerName: 'Họ và tên',
            minWidth: 130,
            cellClass: 'center-cell-class',
        },
        {
            field: 'confirm',
            headerName: 'UVXN',
            width: 70,
            cellRenderer: TestResultCellRendererComponent,
            cellStyle: { 'text-align': 'center' },
        },
        {
            field: 'resultTakenTime',
            headerName: 'Xác thực lúc',
            width: 120,
            cellRenderer: TestResultCellRendererComponent,
            cellClass: 'center-cell-class',
        },
        {
            field: 'examinerName',
            headerName: 'Người phỏng vấn',
            width: 150,
            cellClass: 'center-cell-class',
        },
        {
            field: 'prerequisiteTranslated',
            headerName: 'Điểm sàng lọc',
            width: 120,
            cellClass: 'center-cell-class',
        },
        {
            field: 'scoreTranslated',
            headerName: 'Điểm đánh giá (%)',
            width: 140,
            cellClass: 'right-cell-class',
        },
        {
            field: 'resultTranslated',
            headerName: 'KQ đánh giá',
            width: 100,
            cellRenderer: TestResultCellRendererComponent,
            cellClass: 'center-cell-class',
        },
        {
            field: 'resultCheckerName',
            headerName: 'Người đánh giá',
            width: 150,
            cellClass: 'center-cell-class',
        },
        {
            field: 'resultPDFView',
            headerName: 'Xem kết quả',
            width: 150,
            cellRenderer: TestResultCellRendererComponent,
            cellClass: 'center-cell-class',
        }
    ];

    static generateColumDefs = (round: RoundData, isTest: boolean = false): ColDef[] => {
        if (isEmpty(round)) {
            return [];
        }

        const genericTable = isTest
            ? CampaignUtils.genericTestResultColDefs.filter(
                  (def) => def.field !== 'examinerName'
              )
            : CampaignUtils.genericTestResultColDefs;

        const newColumnDefs = round.active
            ? [CampaignUtils.checkBoxCol, ...genericTable]
            : [...genericTable];

        const finalColumnDefs = cloneDeep(newColumnDefs);
        finalColumnDefs.forEach((colDef) => {
            (colDef as any).roundId = get(round, 'id', '');
        });

        return finalColumnDefs;
    };

    static convertToTestResultTableRow = (
        rawItem: any,
        round: RoundData = {},
        translations: TranslationsMap = {}
    ): TestResultTableRow => {
        const isAutoEvaluation =
            (get(round, 'evaluationType') || '') ===
            ResultEvaluationTypeEnum.AUTO;
        const item = cloneDeep(rawItem);
        const {
            birthday = 0,
            firstName = '',
            lastName = '',
            certificates = [],
            address,
            currentAddress,
            experiences = [],
            authorized = false,
            prerequisite = {},
            score = 0,
            result = CandidateEvaluationEnum.NOT_AVAILABLE,
            gender = GenderEnum.UNKNOWN,
            healthCertificate = {},
            examiner = {},
            resultChecker = {},
            profile = {},
        } = item;

        const getPrerequisiteString = ({ score: prerequisiteScore = 0, total = 0 }: Prerequisite): string => {
            if (isEmpty(prerequisite)) {
                return '';
            }
            if (!total) {
                return translations['NOT_EVALUATED'] || 'Không đánh giá';
            }

            return prerequisiteScore === total
                ? translations['PASSED_LABEL'] || 'Đạt'
                : translations['FAILED_LABEL'] || 'Không đạt';
        };

        const getHealthCertificateType = (healthCertType: string): string => {
            if (isEmpty(healthCertType)) {
                return '';
            }
            const translationKey = CampaignUtils.healthCertificateTypeMapping[healthCertType];
            if (translationKey) {
                const keys = translationKey.split('.');
                const translationValue = get(translations, keys.join('.'));
                return translationValue || healthCertType;
            }
            return healthCertType;
        };

        const prerequisiteString = authorized
            ? getPrerequisiteString(prerequisite)
            : '';

        const age =
            new Date().getFullYear() - new Date(birthday).getFullYear() + '';

        const examinerFirstName = get(examiner, 'firstName') || '';
        const examinerLastName = get(examiner, 'lastName') || '';

        const resultCheckerFirstName = get(resultChecker, 'firstName') || '';
        const resultCheckerLastName = get(resultChecker, 'lastName') || '';

        let certificateStrings = '';

        try {
            certificateStrings = certificates.map((certificate: Certificate) =>
                CampaignUtils.readCertificateInfo(certificate)
            );
        } catch (e) {
            console.log({ e });
        }

        let experienceStrings = '';

        try {
            experienceStrings = experiences.map((experience: Experience) =>
                CampaignUtils.readExperience(experience)
            );
        } catch (e) {
            console.log({ e });
        }

        return {
            age,
            fullName: `${lastName} ${firstName}`,
            certificateStrings,
            experienceStrings,
            ...profile,
            ...item,
            address: CampaignUtils.readAddress(address),
            currentAddress: CampaignUtils.readAddress(currentAddress),
            prerequisiteTranslated: prerequisiteString,
            scoreTranslated: authorized ? CampaignUtils.formatScore(score) : '',
            result,
            resultTranslated: authorized
                ? (CandidateEvaluationEnum2LabelMapping[result as CandidateEvaluationEnum] as string)
                : '',
            gender: GenderEnum2LabelMapping[gender as GenderEnum],
            health: getHealthCertificateType(healthCertificate.type),
            examinerName: `${examinerLastName} ${examinerFirstName}`,
            resultCheckerName: authorized
                ? !isAutoEvaluation
                    ? `${resultCheckerLastName} ${resultCheckerFirstName}`
                    : translations['EVALUATION_AUTO'] || 'Tự động'
                : '',
        };
    };

    static readCertificateInfo(certificate: Certificate | null | undefined): string {
        if (isEmpty(certificate) || !certificate) {
            return '';
        }
        let certificateString = certificate.type || '';

        if (certificate.technique && certificate.technique.length) {
            certificateString += ' - ' + certificate.technique;
        }

        if (certificate.issuer && certificate.issuer.length) {
            certificateString += ' - ' + certificate.issuer;
        }

        if (certificate.date) {
            const momentDate = (moment as any)(new Date(certificate.date));
            certificateString +=
                ' - ' + momentDate.format('DD/MM/YYYY');
        }

        return certificateString;
    }

    static readExperience(exp: Experience | null | undefined): string {
        if (isEmpty(exp) || !exp) {
            return '';
        }

        let expString = '';
        const nYear = exp.year ? `${exp.year} năm ` : '';
        const nMonth = exp.month ? `${exp.month} tháng ` : '';
        expString += nYear + nMonth;

        if (exp.industry && exp.industry.length) {
            expString += ' - ' + exp.industry;
        }

        if (exp.company && exp.company.length) {
            expString += ' - ' + exp.company;
        }

        return expString;
    }

    static readAddress(addressObj: Address | string | null | undefined): string {
        if (isEmpty(addressObj)) {
            return '';
        }
        if (typeof addressObj === 'string') {
            return addressObj;
        }
        const { address = '', town = '', district = '', province = '' } = (addressObj as Address);
        return `${isEmpty(address) ? '' : address + ', '}
        ${isEmpty(town) ? '' : town + ', '}${
            isEmpty(district) ? '' : district + ', '
        }${isEmpty(province) ? '' : province}`;
    }

    static sortByCustomCriteria = (list: any[] = [], criteria: SortCriteria[] = []): any[] =>
        list.sort((a, b) => {
            let scoreA = 0;
            let scoreB = 0;
            const n = criteria.length;
            criteria.forEach((c, i) => {
                const { path = '', isAsc = false } = c;
                if (isEmpty(path)) {
                    return;
                }
                const valA = get(a, path, 0) || 0;
                const valB = get(b, path, 0) || 0;
                if (valA === valB) {
                    return;
                }
                const score = 10 ** (n - i);
                if (!isAsc) {
                    if (valA > valB) {
                        scoreB += score;
                    } else {
                        scoreA += score;
                    }
                } else {
                    if (valA < valB) {
                        scoreB += score;
                    } else {
                        scoreA += score;
                    }
                }
            });

            return scoreA - scoreB;
        });

    static getFeedback(campaign: any): string {
        const feedBackText =
            get(campaign, 'feedback.generalInformationFeedback') || '';
        return isEmpty(campaign.resultFeedback)
            ? feedBackText
            : campaign.resultFeedback;
    }

    static getImagesFromCandidate(profileImages: ProfileImage[], candidate: CandidateProfile): void {
        try {
            const healthImages: any[] =
                get(candidate, 'originalProfile.healthCertificate.images') || [];
            const personalInformationImages: any[] =
                get(candidate, 'originalProfile.personalInformation.images') || [];
            const identityCardImages: any[] =
                get(candidate, 'originalProfile.identityCard.images') || [];
            const applicationImages: any[] =
                get(
                    candidate,
                    'originalProfile.curriculumVitae.applicationImages'
                ) || [];
            const householdImages: any[] =
                get(candidate, 'originalProfile.householdImages') || [];
            const certificate: Certificate[] =
                get(candidate, 'originalProfile.certificate') || [];

            certificate.forEach(({ images = [] }: any, index: number) => {
                if (!isEmpty(images)) {
                    images.forEach((item: any, i: number) => {
                        if (!isEmpty(item)) {
                            profileImages.push({
                                translation: 'CERTIFICATE',
                                name: ' (' + (index + 1) + '-' + (i + 1) + ')',
                                url: item.url,
                            });
                        }
                    });
                }
            });

            healthImages.forEach((item: any, i: number) => {
                if (!isEmpty(item)) {
                    profileImages.push({
                        translation: 'HEALTH_CERTIFICATE',
                        name: ` (${i + 1})`,
                        url: item.url,
                    });
                }
            });

            householdImages.forEach((item: any, i: number) => {
                if (!isEmpty(item)) {
                    profileImages.push({
                        translation: 'PROFILE',
                        name: ` (${i + 1})`,
                        url: item.url,
                    });
                }
            });

            if (isEmpty(householdImages)) {
                personalInformationImages.forEach((item: any, i: number) => {
                    if (!isEmpty(item)) {
                        profileImages.push({
                            translation: 'PROFILE',
                            name: ` (${i + 1})`,
                            url: item.url,
                        });
                    }
                });
            }

            applicationImages.forEach((item: any, i: number) => {
                if (!isEmpty(item)) {
                    profileImages.push({
                        translation: 'APPLICATION_FORM',
                        name: ` (${i + 1})`,
                        url: item.url,
                    });
                }
            });

            identityCardImages.forEach((item: any, i: number) => {
                if (!isEmpty(item)) {
                    profileImages.push({
                        translation: i > 0 ? 'ID_CARD_BACK' : 'ID_CARD_FRONT',
                        name: '',
                        url: item.url,
                    });
                }
            });
        } catch (e) {
            console.log('Error while getting images from candidate', e);
        }
    }

    static updateHeaderTranslation(colDefs: ColDef[] = [], translation: HeaderTranslation = {}): void {
        this.genericTestResultColDefs.forEach((c: ColDef) => {
            try {
                const field = c.field as string;
                c.headerName = translation[field];
            } catch (e) {
                console.log('not found translation for ', { c, e });
            }
        });

        colDefs.forEach((c: ColDef) => {
            try {
                const field = c.field as string;
                c.headerName = translation[field];
            } catch (e) {
                console.log('not found translation for ', { c, e });
            }
        });
    }

    static formatScore(score: number): string {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(score);
    }
}
